import type { IsoTimestamp, Uuid } from "../types/base.js";
import type { Trip, TripItem } from "../types/entities.js";

export interface MonthTotal {
  /** Monat als "2026-09". Sortierbar und ohne Zeitzonen-Ärger. */
  month: string;
  label: string;
  totalCents: number;
  trips: number;
}

export interface StoreShare {
  storeId: Uuid | null;
  totalCents: number;
  share: number;
}

export interface BudgetStatus {
  spentCents: number;
  budgetCents: number | null;
  /** Anteil des Budgets, 0 bis über 1. Ohne Budget null. */
  ratio: number | null;
  remainingCents: number | null;
  level: "gut" | "knapp" | "drüber";
}

const MONAT = new Intl.DateTimeFormat("de-DE", { month: "short", year: "numeric" });

function monthKey(iso: IsoTimestamp): string {
  return iso.slice(0, 7);
}

/**
 * Ausgaben je Monat, älteste zuerst. Monate ohne Einkauf bleiben drin, sonst
 * sieht ein Balkendiagramm aus, als wäre nie eine Pause gewesen.
 */
export function monthlyTotals(trips: readonly Trip[], months = 12, now = new Date()): MonthTotal[] {
  const relevant = trips.filter((trip) => trip.deleted_at === null && trip.finished_at !== null);

  const buckets = new Map<string, { totalCents: number; trips: number }>();
  for (let i = months - 1; i >= 0; i--) {
    const date = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    buckets.set(date.toISOString().slice(0, 7), { totalCents: 0, trips: 0 });
  }

  for (const trip of relevant) {
    const key = monthKey(trip.finished_at ?? trip.started_at);
    const bucket = buckets.get(key);
    if (!bucket) continue;
    bucket.totalCents += trip.total_cents ?? 0;
    bucket.trips += 1;
  }

  return [...buckets.entries()].map(([month, value]) => ({
    month,
    label: MONAT.format(new Date(`${month}-01T00:00:00.000Z`)),
    totalCents: value.totalCents,
    trips: value.trips,
  }));
}

/** Anteil je Laden in einem Monat, größter zuerst. */
export function storeShares(trips: readonly Trip[], month: string): StoreShare[] {
  const relevant = trips.filter(
    (trip) => trip.deleted_at === null && trip.finished_at !== null && monthKey(trip.finished_at ?? "") === month,
  );

  const gesamt = relevant.reduce((sum, trip) => sum + (trip.total_cents ?? 0), 0);
  if (gesamt === 0) return [];

  const byStore = new Map<Uuid | null, number>();
  for (const trip of relevant) {
    byStore.set(trip.store_id, (byStore.get(trip.store_id) ?? 0) + (trip.total_cents ?? 0));
  }

  return [...byStore.entries()]
    .map(([storeId, totalCents]) => ({ storeId, totalCents, share: totalCents / gesamt }))
    .sort((a, b) => b.totalCents - a.totalCents);
}

/**
 * Wo das Monatsbudget steht. Ab 80 Prozent wird es knapp, ab 100 ist es
 * überschritten. Ohne Budget gibt es keine Bewertung, nur die Summe.
 */
export function budgetStatus(trips: readonly Trip[], budgetCents: number | null, month: string): BudgetStatus {
  const spentCents = trips
    .filter((trip) => trip.deleted_at === null && trip.finished_at !== null && monthKey(trip.finished_at ?? "") === month)
    .reduce((sum, trip) => sum + (trip.total_cents ?? 0), 0);

  if (budgetCents === null || budgetCents <= 0) {
    return { spentCents, budgetCents: null, ratio: null, remainingCents: null, level: "gut" };
  }

  const ratio = spentCents / budgetCents;
  return {
    spentCents,
    budgetCents,
    ratio,
    remainingCents: budgetCents - spentCents,
    level: ratio >= 1 ? "drüber" : ratio >= 0.8 ? "knapp" : "gut",
  };
}

/** Geschätzte Summe der offenen Artikel, aus dem zuletzt bezahlten Preis. */
export function estimateOpenTotal(
  items: readonly { product_id: Uuid | null; is_checked: boolean; deleted_at: IsoTimestamp | null }[],
  prices: ReadonlyMap<Uuid, number>,
): number {
  return items
    .filter((item) => item.deleted_at === null && !item.is_checked)
    .reduce((sum, item) => sum + (item.product_id ? (prices.get(item.product_id) ?? 0) : 0), 0);
}

/** Positionen eines Einkaufs, teuerste zuerst. Für die Detailansicht. */
export function tripPositions(tripItems: readonly TripItem[], tripId: Uuid): TripItem[] {
  return tripItems
    .filter((item) => item.trip_id === tripId && item.deleted_at === null)
    .sort((a, b) => (b.price_cents ?? 0) - (a.price_cents ?? 0) || a.name.localeCompare(b.name, "de-DE"));
}
